import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { Header, Label } from 'common/components/texts/Texts';
import { useTranslation } from 'react-i18next';
import Button from 'common/components/button/Button';
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'common/components/modal/Modal';
import SearchableSelectInput from 'common/components/searchableSelectInput/SearchableSelectInput';
import { OptionTitleSubTitle, SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import { Col, Row } from 'react-bootstrap';
import { WorkExternalPartContactDto, WorkExternalPartDto } from 'api/works/models/WorkDto';
import SelectInput, { SelectInputOption } from 'common/components/selectInput/SelectInput';
import { useForm } from 'react-hook-form';
import { TextInputController } from 'common/components/textInput/TextInput';
import InputError from 'common/components/inputError/InputError';
import { PhoneCodeOption, PhoneInputController } from 'common/components/phoneInput/PhoneInput';
import Toast from 'common/services/Toast';

type IndependentContactForm = {
    name?: string;
    function?: string;
    email?: string;
    phoneNumber?: string;
    phoneCodeId?: string;
};

export type ContactModalMode =
    | { kind: 'add' }
    | { kind: 'edit-independent'; contact: WorkExternalPartContactDto };

export type ContactModalResult =
    | { kind: 'thirdParty'; id: string }
    | { kind: 'independent'; contact: Omit<WorkExternalPartContactDto, 'key'> };

type Props = {
    mode: ContactModalMode;
    onLoadItems: (inputValue: string, callback: (options: SearchableSelectTitleSubTitleOption[], hasMore: boolean) => void) => void;
    onClose: (result?: ContactModalResult) => void;
    selectedExternalParts: (WorkExternalPartDto)[];
    phoneCodesOptions: PhoneCodeOption[];
} & ReactModal.Props;

const ContactModal: React.FC<Props> = ({ mode, onLoadItems, onClose, selectedExternalParts, phoneCodesOptions, ...props }: Props) => {
    const { t } = useTranslation();

    const isEditMode = mode.kind === 'edit-independent';
    const noExternalParts = selectedExternalParts.length === 0;
    const [contact, setContact] = useState<string | undefined>(undefined);
    const disableInlineFields = !isEditMode && !!contact;

    const [externalPartSelected, setExternalPartSelected] = useState<string | undefined>(undefined);
    const [showExternalPartDrop, setShowExternalPartDrop] = useState<boolean>(false);

    const inlineForm = useForm<IndependentContactForm>({ shouldUnregister: false });
    const errors = inlineForm.formState.errors;

    useEffect(() => {
        if (!props.isOpen) return;
        if (mode.kind === 'edit-independent') {
            inlineForm.reset({
                name: mode.contact.name,
                function: mode.contact.function,
                email: mode.contact.email,
                phoneNumber: mode.contact.phoneNumber,
                phoneCodeId: mode.contact.phoneCodeId,
            });
        } else {
            inlineForm.reset({ name: '', function: '', email: '', phoneNumber: '', phoneCodeId: undefined });
        }
        setContact(undefined);
        setExternalPartSelected(undefined);
        setShowExternalPartDrop(false);
    }, [props.isOpen, mode]);

    const onAddNewContact = () => {
        const externalPart = selectedExternalParts.find(x => x.externalPartId === externalPartSelected);
        window.open('/external-parts/' + externalPart?.externalPartCategoryId + '/edit/' + externalPart?.externalPartId);
        setShowExternalPartDrop(false);
        setExternalPartSelected(undefined);
    }

    const onClickRefreshAfterNew = () => {
        setShowExternalPartDrop(false);
        setExternalPartSelected(undefined);
    }

    const closeModal = (result?: ContactModalResult) => {
        setShowExternalPartDrop(false);
        setExternalPartSelected(undefined);
        setContact(undefined);
        inlineForm.reset({ name: '', function: '', email: '', phoneNumber: '', phoneCodeId: undefined });
        onClose(result);
    }

    const onSave = async () => {
        const values = inlineForm.getValues();
        const hasInlineName = !!(values.name && values.name.trim().length > 0);

        if (!isEditMode && contact && hasInlineName) {
            Toast.warning(t('works.work.contact_pick_one'));
            return;
        }

        if (!isEditMode && contact && !hasInlineName) {
            closeModal({ kind: 'thirdParty', id: contact });
            return;
        }

        if (!isEditMode && !contact && !hasInlineName) {
            Toast.warning(t('works.work.contact_required'));
            return;
        }

        const valid = await inlineForm.trigger();
        if (!valid) {
            return;
        }

        const built: Omit<WorkExternalPartContactDto, 'key'> = {
            externalPartContactId: null,
            name: values.name,
            function: values.function,
            email: values.email,
            phoneNumber: values.phoneNumber,
            phoneCodeId: values.phoneCodeId,
            phoneCode: phoneCodesOptions.find(p => p.value === values.phoneCodeId)?.phoneCode,
        };

        closeModal({ kind: 'independent', contact: built });
    }

    return (
        <Modal {...props} style={{ content: { minWidth: 720 } }} onRequestClose={() => { closeModal(); }}>
            <div>
                <ModalHeader>
                    <Header>{(isEditMode ? t('works.work.edit_contact') : t('works.work.contacts')).toUpperCase()}</Header>
                </ModalHeader>
                <ModalBody>
                    {!isEditMode && (
                        <>
                            <Row className='mb-2'>
                                <Label>{t('works.work.select_third_party_contact')}</Label>
                                {noExternalParts && (
                                    <small className='text-muted'>{t('works.work.external_parts_required')}</small>
                                )}
                                <SearchableSelectInput
                                    value={contact}
                                    loadOptions={onLoadItems}
                                    onChange={(item) => {
                                        setContact(item ?? undefined);
                                        setShowExternalPartDrop(false);
                                        if (item) {
                                            inlineForm.reset({ name: '', function: '', email: '', phoneNumber: '', phoneCodeId: undefined });
                                        }
                                    }}
                                    disabled={noExternalParts}
                                    formatOptionLabel={OptionTitleSubTitle}
                                    onClickAddNew={contact ? undefined : () => setShowExternalPartDrop(true)}
                                    onClickRefreshAfterNew={onClickRefreshAfterNew}
                                />
                            </Row>
                            {showExternalPartDrop && <Row className='mb-3'>
                                <Label>{t('works.work.select_external_part')}</Label>
                                <SelectInput
                                    value={externalPartSelected}
                                    options={selectedExternalParts.map(item => ({
                                        label: item.externalPartName ?? '',
                                        value: item.externalPartId!,
                                    } satisfies SelectInputOption))}
                                    onChange={(item) => {
                                        setExternalPartSelected(item ?? undefined);
                                    }}
                                    formatOptionLabel={OptionTitleSubTitle}
                                    onClickAddNew={externalPartSelected ? () => onAddNewContact() : undefined}
                                    showAddWhenSelected={true}
                                />
                            </Row>}
                            <Row className='mb-3 mt-4'>
                                <Label>{t('works.work.or_create_new_contact')}</Label>
                            </Row>
                        </>
                    )}
                    <Row>
                        <Col sm={12} md={6} className='mb-3'>
                            <Label space>{t('external_parts.contact.name_last_name')}{isEditMode ? '*' : ''}</Label>
                            <TextInputController
                                name='name'
                                control={inlineForm.control}
                                placeholder={t('external_parts.contact.name_last_name')}
                                rules={{ required: isEditMode, maxLength: 250 }}
                                hasError={Boolean(errors.name)}
                                disabled={disableInlineFields}
                            />
                            <InputError error={errors.name} maxLength={250} />
                        </Col>
                        <Col sm={12} md={6} className='mb-3'>
                            <Label space>{t('external_parts.contact.function')}</Label>
                            <TextInputController
                                name='function'
                                control={inlineForm.control}
                                placeholder={t('external_parts.contact.function')}
                                rules={{ maxLength: 250 }}
                                hasError={Boolean(errors.function)}
                                disabled={disableInlineFields}
                            />
                            <InputError error={errors.function} maxLength={250} />
                        </Col>
                        <Col sm={12} md={6} className='mb-3'>
                            <Label space>{t('external_parts.contact.phone_number')}</Label>
                            <PhoneInputController
                                name='phoneNumber'
                                control={inlineForm.control}
                                onChange={() => { }}
                                phoneCodes={phoneCodesOptions}
                                phoneCodeValueName='phoneCodeId'
                                disabled={disableInlineFields}
                            />
                            <InputError error={errors.phoneNumber} />
                        </Col>
                        <Col sm={12} md={6} className='mb-3'>
                            <Label space>{t('external_parts.contact.email')}</Label>
                            <TextInputController
                                name='email'
                                control={inlineForm.control}
                                type='email'
                                placeholder={t('external_parts.contact.email')}
                                rules={{
                                    maxLength: 250,
                                    pattern: { value: /\S+@\S+\.\S+/, message: 'email' },
                                }}
                                hasError={Boolean(errors.email)}
                                disabled={disableInlineFields}
                            />
                            <InputError error={errors.email} maxLength={250} />
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter className='text-right'>
                    <Button fw variant='secondary' onClick={() => { closeModal(); }}>{t('common.cancel')}</Button>
                    <Button onClick={() => { void onSave(); }}>{t('common.confirm')}</Button>
                </ModalFooter>
            </div>
        </Modal>
    );
};

export default ContactModal;
